import React, { useEffect, useRef, useState } from 'react';
import { X, Play, Pause, Heart, Share2, Bookmark, Sparkles, Volume2, VolumeX, Check } from 'lucide-react';
import { Button } from './ui/button';
import { useLang } from '../context/LangContext';
import { useProject } from '../context/ProjectContext';
import { useEditor } from '../context/EditorContext';
import { modalI18n } from '../mock/data';
import { toast } from '../hooks/use-toast';

const TemplateModal = ({ template, onClose }) => {
  const { lang, t } = useLang();
  const { addTemplate, templates } = useProject();
  const { openEditorWithVideo } = useEditor();
  const m = modalI18n[lang];
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => setProgress(v.duration ? (v.currentTime / v.duration) * 100 : 0);
    v.addEventListener('timeupdate', onTime);
    return () => v.removeEventListener('timeupdate', onTime);
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) { v.pause(); setPlaying(false); } else { v.play(); setPlaying(true); }
  };

  const alreadyAdded = templates.find(x => x.id === template.id);

  const useTemplate = () => {
    addTemplate({ id: template.id, title: template.title, category: template.category });
    // Open editor pre-loaded with the template video so user can immediately edit
    openEditorWithVideo({
      videoUrl: template.video,
      videoName: template.title,
      settings: template.settings || null
    });
    toast({ title: lang === 'sv' ? 'Öppnar mall i redigeraren...' : 'يفتح القالب في المحرر...', description: `${template.title} — ${template.category}` });
    setTimeout(onClose, 200);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 animate-fade-up" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />
      <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden max-w-5xl w-full max-h-[92vh] grid md:grid-cols-[minmax(0,1fr)_360px]" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 rtl:right-auto rtl:left-4 z-10 w-10 h-10 rounded-full bg-black/60 hover:bg-black text-white flex items-center justify-center transition-colors" aria-label="close">
          <X className="w-5 h-5" />
        </button>

        {/* Video side */}
        <div className="relative bg-black min-h-[280px] md:min-h-[560px] flex items-center justify-center overflow-hidden">
          {videoFailed ? (
            <img src={template.img} alt={template.title} className="w-full h-full object-cover max-h-[92vh]" />
          ) : (
            <video
              ref={videoRef}
              src={template.video}
              poster={template.img}
              autoPlay
              loop
              muted={muted}
              playsInline
              onError={() => setVideoFailed(true)}
              className="w-full h-full object-cover max-h-[92vh]"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />

          {/* Top badges */}
          <div className="absolute top-4 left-4 rtl:left-auto rtl:right-4 flex items-center gap-2">
            <div className="bg-white/95 text-neutral-900 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase">{template.category}</div>
            <div className="bg-black/60 backdrop-blur text-white rounded-full px-2.5 py-1 text-[11px] font-semibold">{template.duration}</div>
          </div>

          {/* Center play button when paused */}
          {!playing && (
            <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl">
                <Play className="w-8 h-8 fill-black text-black ml-1" />
              </div>
            </button>
          )}

          {/* Bottom controls */}
          <div className="absolute bottom-0 inset-x-0 p-4 flex items-center gap-3">
            <button onClick={togglePlay} className="w-10 h-10 rounded-full bg-white/95 flex items-center justify-center">
              {playing ? <Pause className="w-4 h-4 fill-black text-black" /> : <Play className="w-4 h-4 fill-black text-black ml-0.5" />}
            </button>
            <div className="flex-1 h-1 bg-white/25 rounded-full overflow-hidden">
              <div className="h-full bg-white rounded-full transition-[width] duration-200" style={{ width: `${progress}%` }} />
            </div>
            <button onClick={() => setMuted(v => !v)} className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur text-white flex items-center justify-center">
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Info side */}
        <div className="p-6 md:p-8 flex flex-col overflow-y-auto">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-cyan-500" />
            <span className="text-[12px] font-bold uppercase tracking-wider text-cyan-600">{lang === 'sv' ? 'Trendmall' : 'قالب رائج'}</span>
          </div>
          <h2 className="text-3xl font-black tracking-tight text-neutral-900 mb-2">{template.title}</h2>
          <p className="text-[14px] text-neutral-600 leading-relaxed mb-6">{m.description}</p>

          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-neutral-50 rounded-xl p-3">
              <div className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider mb-1">{m.duration}</div>
              <div className="text-[15px] font-bold text-neutral-900">{template.duration}</div>
            </div>
            <div className="bg-neutral-50 rounded-xl p-3">
              <div className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider mb-1">{m.uses}</div>
              <div className="text-[15px] font-bold text-neutral-900">{template.uses}</div>
            </div>
            <div className="bg-neutral-50 rounded-xl p-3">
              <div className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider mb-1">{m.clips}</div>
              <div className="text-[15px] font-bold text-neutral-900">6</div>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 mb-6">
            {template.tags?.map((tag, i) => (
              <span key={i} className="text-[11.5px] font-semibold text-neutral-700 bg-neutral-100 rounded-full px-2.5 py-1">#{tag}</span>
            ))}
          </div>

          <div className="mt-auto space-y-3">
            <Button onClick={useTemplate} disabled={!!alreadyAdded} className="w-full h-12 rounded-full bg-black hover:bg-neutral-800 text-white font-bold text-[14.5px] disabled:bg-emerald-600 disabled:opacity-100">
              {alreadyAdded ? (<><Check className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" /> {lang === 'sv' ? 'Tillagd i projekt' : 'تمت إضافة القالب'}</>) : (<>{m.useNow}</>)}
            </Button>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setLiked(v => !v)} className={`h-11 rounded-full font-semibold text-[13px] flex items-center justify-center gap-1.5 transition-colors ${liked ? 'bg-rose-50 text-rose-600 border border-rose-200' : 'bg-neutral-100 text-neutral-800 hover:bg-neutral-200'}`}>
                <Heart className={`w-4 h-4 ${liked ? 'fill-rose-500 text-rose-500' : ''}`} /> {m.likes}
              </button>
              <button onClick={() => setSaved(v => !v)} className={`h-11 rounded-full font-semibold text-[13px] flex items-center justify-center gap-1.5 transition-colors ${saved ? 'bg-amber-50 text-amber-600 border border-amber-200' : 'bg-neutral-100 text-neutral-800 hover:bg-neutral-200'}`}>
                <Bookmark className={`w-4 h-4 ${saved ? 'fill-amber-500 text-amber-500' : ''}`} /> {m.save}
              </button>
              <button className="h-11 rounded-full font-semibold text-[13px] flex items-center justify-center gap-1.5 bg-neutral-100 text-neutral-800 hover:bg-neutral-200 transition-colors">
                <Share2 className="w-4 h-4" /> {m.share}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateModal;
